import { useEffect, useRef, useState } from "react";
import { getProjetosAcompanhamento } from "../../api/projetos.api";
import TicketDrawer from "../../components/TicketDrawer";

const REFRESH_SEC = 120;

/* ── Helpers ─────────────────────────────────────── */
function pctColor(v) {
  if (v === null || v === undefined) return "#475569";
  if (v >= 70) return "#4ade80";
  if (v >= 40) return "#fbbf24";
  return "#f87171";
}

function importanciaFaixa(v) {
  if (!v && v !== 0) return "sem";
  if (v >= 700) return "critico";
  if (v >= 300) return "atencao";
  return "normal";
}

const FAIXA_CFG = {
  critico: { label: "Crítico",  color: "#f87171", bg: "rgba(248,113,113,0.1)",  border: "rgba(248,113,113,0.25)" },
  atencao: { label: "Atenção",  color: "#fbbf24", bg: "rgba(251,191,36,0.1)",   border: "rgba(251,191,36,0.25)"  },
  normal:  { label: "Normal",   color: "#4ade80", bg: "rgba(74,222,128,0.1)",   border: "rgba(74,222,128,0.25)"  },
  sem:     { label: "Sem dado", color: "#475569", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.12)" },
};

const NIVEL_COLORS = ["#60a5fa", "#a78bfa", "#22d3ee", "#fbbf24", "#4ade80"];
const nc = (d) => NIVEL_COLORS[Math.min(d, NIVEL_COLORS.length - 1)];

const GP_STATUS = {
  1:  { label: "Em dia",                       color: "#4ade80"  },
  2:  { label: "Não iniciado",                 color: "#94a3b8"  },
  3:  { label: "Parado",                       color: "#f87171"  },
  4:  { label: "Em Atraso",                    color: "#f87171"  },
  5:  { label: "Parado Cliente",               color: "#fb923c"  },
  6:  { label: "Parado Lexsis Produção",       color: "#fb923c"  },
  7:  { label: "Parado Lexsis Implantação",    color: "#fb923c"  },
  8:  { label: "Parado Lexsis Comercial",      color: "#fb923c"  },
  9:  { label: "Não Iniciado Com DeadLine",    color: "#fbbf24"  },
  10: { label: "Não Iniciado Sem DeadLine",    color: "#94a3b8"  },
  12: { label: "Aguardando Feedback",          color: "#a78bfa"  },
  13: { label: "Em Produção - Acompanhamento", color: "#22d3ee"  },
  14: { label: "Em andamento",                 color: "#60a5fa"  },
  15: { label: "Fila homologação",             color: "#fbbf24"  },
  16: { label: "Finalizado",                   color: "#4ade80"  },
  17: { label: "Restrição",                    color: "#f87171"  },
  18: { label: "Em Piloto",                    color: "#22d3ee"  },
  19: { label: "Em Testes",                    color: "#a78bfa"  },
  20: { label: "Em desenvolvimento",           color: "#60a5fa"  },
};
const gpStatus = (id) => GP_STATUS[id] ?? { label: "—", color: "#334155" };

const TICKET_FECHADOS = new Set([
  "Fechado", "Resolvido", "Em Teste", "Aguardando Testes",
  "Aguardando Versão", "Ajuste Especificação", "Feedback",
]);
const isTicketFechado = (t) =>
  t.ticket_fechado || (t.ticket_status && TICKET_FECHADOS.has(t.ticket_status));

const FINISHED_STATUS_IDS = new Set([12, 15, 16, 18, 19]);

function calcPct(proj) {
  if (FINISHED_STATUS_IDS.has(proj.id_gp_status)) return 100;
  if (proj.children && proj.children.length > 0) {
    const vals = proj.children.map(c => calcPct(c));
    return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
  }
  if (proj.tickets && proj.tickets.length > 0)
    return Math.round(proj.tickets.filter(t => isTicketFechado(t)).length / proj.tickets.length * 100);
  return proj.percentual_concluido ?? 0;
}

function buildTree(map) {
  const roots = [];
  for (const p of map.values()) {
    if (p.id_projeto_pai && map.has(p.id_projeto_pai)) map.get(p.id_projeto_pai).children.push(p);
    else roots.push(p);
  }
  const sort = (arr) => { arr.sort((a, b) => (b.importancia || 0) - (a.importancia || 0)); arr.forEach(p => sort(p.children)); };
  sort(roots);
  return roots;
}

function flatTree(nodes, depth = 0, result = []) {
  for (const n of nodes) { result.push({ ...n, _depth: depth }); flatTree(n.children, depth + 1, result); }
  return result;
}

function buildStats(rows) {
  const projetoMap = new Map();
  for (const row of rows) {
    const k = row.id_projeto;
    if (!projetoMap.has(k)) {
      projetoMap.set(k, {
        id_projeto:           row.id_projeto,
        id_projeto_pai:       row.id_projeto_pai,
        descricao:            row.descricao,
        nivel:                row.nivel,
        importancia:          row.importancia,
        deadline:             row.deadline,
        percentual_concluido: row.percentual_concluido,
        id_gp_status:         row.id_gp_status,
        tickets:              [],
        children:             [],
      });
    }
    const ticketId = row.ticketid || row.ticketId || row.id_ticket_prog;
    if (ticketId) {
      projetoMap.get(k).tickets.push({
        id:             ticketId,
        subject:        row.subject || null,
        desenvolvedor:  row.desenvolvedor,
        ticket_fechado: row.ticket_fechado,
        ticket_status:  row.ticket_status || null,
        sistema:        row.sistema,
        done_ratio:     row.done_ratio,
      });
    }
  }

  const roots    = buildTree(projetoMap);
  const projetos = flatTree(roots);

  const totalProjetos  = projetos.length;
  const raizes         = projetos.filter(p => !p.id_projeto_pai);
  const totalTickets   = projetos.reduce((s, p) => s + p.tickets.length, 0);
  const totalFechados  = projetos.reduce((s, p) => s + p.tickets.filter(t => isTicketFechado(t)).length, 0);
  const totalAbertos   = totalTickets - totalFechados;
  const pctGeral       = totalTickets ? Math.round((totalFechados / totalTickets) * 100) : 0;

  const comDados = projetos.filter(p => p.tickets.length > 0 || p.percentual_concluido != null || FINISHED_STATUS_IDS.has(p.id_gp_status));
  const pctMedio = comDados.length
    ? Math.round(comDados.reduce((s, p) => s + calcPct(p), 0) / comDados.length)
    : 0;

  /* Por importância */
  const porFaixa = { critico: [], atencao: [], normal: [], sem: [] };
  for (const p of projetos) porFaixa[importanciaFaixa(p.importancia)].push(p);

  /* Por desenvolvedor */
  const devMap = new Map();
  for (const proj of projetos) {
    for (const t of proj.tickets) {
      const dev = t.desenvolvedor || "Não atribuído";
      if (!devMap.has(dev)) devMap.set(dev, { total: 0, fechados: 0 });
      devMap.get(dev).total++;
      if (isTicketFechado(t)) devMap.get(dev).fechados++;
    }
  }
  const porDev = Array.from(devMap.entries())
    .map(([nome, s]) => ({ nome, ...s, pct: s.total ? Math.round(s.fechados / s.total * 100) : 0 }))
    .sort((a, b) => b.total - a.total);

  /* Com deadline */
  const comDeadline = projetos.filter(p => p.deadline);

  /* Projetos com progresso baixo (com tickets, pct < 40) — já ordenados por importância via árvore */
  const emRisco = projetos.filter(p => p.tickets.length > 0 && calcPct(p) < 40).slice(0, 5);

  return { projetos, raizes, totalProjetos, totalTickets, totalFechados, totalAbertos, pctGeral, pctMedio, porFaixa, porDev, comDeadline, emRisco };
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function ProjetosDashboard() {
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_SEC);
  const [drawer,    setDrawer]    = useState(null); // { tipo, fetchId }
  const loadRef = useRef(null);
  const isFirst = useRef(true);

  async function load() {
    const first = isFirst.current;
    if (first) setLoading(true);
    setError(false);
    try {
      const raw  = await getProjetosAcompanhamento(false, 1);
      const data = (Array.isArray(raw) ? raw : []).filter(r => r.tipo == null || r.tipo === 1);
      setStats(buildStats(data));
      isFirst.current = false;
    } catch {
      if (first) setError(true);
    } finally {
      if (first) setLoading(false);
    }
  }

  loadRef.current = load;

  function handleRefresh() { loadRef.current(); setCountdown(REFRESH_SEC); }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => { if (prev <= 1) { loadRef.current(); return REFRESH_SEC; } return prev - 1; });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <Skeleton />;
  if (error || !stats) return (
    <div style={errorWrap}>
      <span style={{ fontSize: 32, color: "#fbbf24" }}>⚠</span>
      <p style={{ color: "#94a3b8" }}>Erro ao carregar dados</p>
      <button onClick={handleRefresh} style={btnSec}>Tentar novamente</button>
    </div>
  );

  const { projetos, totalProjetos, totalTickets, totalFechados, totalAbertos, pctGeral, pctMedio, porFaixa, porDev, comDeadline, emRisco } = stats;

  const allDrawerTickets = projetos.flatMap(p =>
    p.tickets.map(t => ({
      id:            t.id,
      subject:       t.subject,
      status:        t.ticket_status || (isTicketFechado(t) ? "Fechado" : "Aberto"),
      desenvolvedor: t.desenvolvedor,
      projeto:       p.descricao,
    }))
  );

  const drawerCfg = {
    total:   { title: "Todos os Tickets",     color: "#94a3b8", filter: () => true },
    fechados:{ title: "Tickets Fechados",     color: "#4ade80", filter: t => isTicketFechado({ ticket_status: t.status }) },
    abertos: { title: "Tickets em Aberto",    color: "#fbbf24", filter: t => !isTicketFechado({ ticket_status: t.status }) },
  };

  function openDrawer(tipo) {
    setDrawer(prev => prev?.tipo === tipo ? null : { tipo, fetchId: Date.now() });
  }

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={headerLeft}>
          <h1 style={h1}>Dashboard de Projetos</h1>
          <span style={badge}>{totalProjetos} projetos ativos</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={handleRefresh} title="Atualizar agora" style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── KPIs ── */}
      <div style={kpiRow}>
        {/* Grupo: Projetos */}
        <div style={kpiGroup}>
          <div style={kpiGroupLabel}>Projetos</div>
          <div style={kpiGroupCards}>
            <KpiCard label="Ativos"          value={totalProjetos}   color="#60a5fa" />
            <KpiCard label="Conclusão Média" value={`${pctMedio}%`} color={pctColor(pctMedio)}
              extra={<MiniBar pct={pctMedio} color={pctColor(pctMedio)} />} />
          </div>
        </div>
        {/* Grupo: Tickets */}
        <div style={{ ...kpiGroup, flex: 3 }}>
          <div style={kpiGroupLabel}>Tickets</div>
          <div style={kpiGroupCards}>
            <KpiCard label="Total"           value={totalTickets}   color="#94a3b8" active={drawer?.tipo === "total"}    onClick={() => openDrawer("total")} />
            <KpiCard label="Fechados"        value={totalFechados}  color="#4ade80" active={drawer?.tipo === "fechados"} onClick={() => openDrawer("fechados")} />
            <KpiCard label="Abertos"         value={totalAbertos}   color="#fbbf24" active={drawer?.tipo === "abertos"}  onClick={() => openDrawer("abertos")} />
            <KpiCard label="Progresso Geral" value={`${pctGeral}%`} color={pctColor(pctGeral)}
              extra={<MiniBar pct={pctGeral} color={pctColor(pctGeral)} />} />
          </div>
        </div>
      </div>

      {/* ── Linha 2: Importância + Em Risco ── */}
      <div style={row2}>

        {/* Distribuição por importância */}
        <div style={{ ...glass, padding: "18px 20px", flex: 1 }}>
          <SectionTitle>Distribuição por Importância</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
            {(["critico", "atencao", "normal", "sem"]).map(faixa => {
              const cfg   = FAIXA_CFG[faixa];
              const items = porFaixa[faixa];
              if (items.length === 0) return null;
              return (
                <div key={faixa}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ ...faixaBadge, color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
                      {cfg.label}
                    </span>
                    <span style={{ color: cfg.color, fontWeight: 800, fontSize: 18 }}>{items.length}</span>
                    <span style={{ color: "#334155", fontSize: 11 }}>projeto{items.length > 1 ? "s" : ""}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {items.map(p => {
                      const depth   = p._depth ?? Math.max(0, (p.nivel || 1) - 1);
                      const isChild = depth > 0;
                      const acent   = nc(depth);
                      return (
                        <div key={p.id_projeto} style={{
                          ...projRow,
                          marginLeft: depth * 20,
                          borderLeft: isChild ? `3px solid ${acent}50` : "3px solid transparent",
                          background: isChild ? `${acent}06` : projRow.background,
                        }}>
                          {isChild && <span style={{ color: acent, fontSize: 9, flexShrink: 0, opacity: 0.7 }}>└</span>}
                          <span style={{ color: isChild ? acent : "#475569", fontSize: 10, flexShrink: 0, fontWeight: 700 }}>#{p.id_projeto}</span>
                          <span style={{ color: isChild ? "#94a3b8" : "#e2e8f0", fontSize: 11, fontWeight: isChild ? 400 : 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {p.descricao}
                          </span>
                          {p.id_gp_status != null && (() => {
                            const st = gpStatus(p.id_gp_status);
                            return <span style={{ padding: "1px 7px", borderRadius: 8, fontSize: 9, fontWeight: 700, border: `1px solid ${st.color}35`, background: `${st.color}12`, color: st.color, whiteSpace: "nowrap", flexShrink: 0 }}>{st.label}</span>;
                          })()}
                          <span style={{ color: pctColor(calcPct(p)), fontSize: 10, fontWeight: 700, flexShrink: 0, minWidth: 32, textAlign: "right" }}>
                            {calcPct(p)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Em risco */}
        {emRisco.length > 0 && (
          <div style={{ ...glass, padding: "18px 20px", minWidth: 280, maxWidth: 340 }}>
            <SectionTitle color="#f87171">⚠ Projetos em Risco</SectionTitle>
            <div style={{ color: "#475569", fontSize: 10, marginBottom: 12 }}>Com tickets e progresso abaixo de 40%</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {emRisco.map(p => {
                const pct = calcPct(p);
                return (
                  <div key={p.id_projeto} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>{p.descricao}</span>
                      <span style={{ color: pctColor(pct), fontSize: 12, fontWeight: 800 }}>{pct}%</span>
                    </div>
                    <MiniBar pct={pct} color={pctColor(pct)} />
                    <div style={{ display: "flex", gap: 6 }}>
                      <span style={{ color: "#475569", fontSize: 10 }}>{p.tickets.length} tickets</span>
                      {p.importancia != null && (
                        <span style={{ color: "#fbbf24", fontSize: 10 }}>· imp. {p.importancia}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Deadlines */}
        {comDeadline.length > 0 && (
          <div style={{ ...glass, padding: "18px 20px", minWidth: 240, maxWidth: 300 }}>
            <SectionTitle color="#fbbf24">📅 Deadlines</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
              {comDeadline.map(p => (
                <div key={p.id_projeto} style={projRow}>
                  <span style={{ color: "#94a3b8", fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.descricao}
                  </span>
                  <span style={{ color: "#fbbf24", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{p.deadline}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Linha 3: Progresso por Projeto ── */}
      <div style={{ ...glass, padding: "18px 20px" }}>
        <SectionTitle>Progresso por Projeto</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
          {projetos.map(p => {
            const depth    = p._depth ?? Math.max(0, (p.nivel || 1) - 1);
            const pct      = calcPct(p);
            const color    = pctColor(pct);
            const acent    = nc(depth);
            const fechados = p.tickets.filter(t => isTicketFechado(t)).length;
            const isChild  = depth > 0;
            return (
              <div key={p.id_projeto} style={{
                display: "grid", gridTemplateColumns: "1fr 3fr 60px", gap: 12, alignItems: "center",
                marginLeft: depth * 28,
                padding: "7px 12px",
                borderRadius: 8,
                borderLeft: isChild ? `3px solid ${acent}` : "3px solid transparent",
                background: isChild ? `linear-gradient(90deg, ${acent}10, transparent)` : "transparent",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  {isChild && <span style={{ color: acent, fontSize: 10, flexShrink: 0 }}>└</span>}
                  <span style={{ ...faixaDot, background: acent, boxShadow: `0 0 5px ${acent}80`, flexShrink: 0 }} />
                  <span style={{ color: isChild ? "#94a3b8" : "#e2e8f0", fontSize: isChild ? 11 : 12, fontWeight: isChild ? 400 : 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.descricao}
                  </span>
                  {p.id_gp_status != null && (() => {
                    const st = gpStatus(p.id_gp_status);
                    return <span style={{ padding: "1px 6px", borderRadius: 6, fontSize: 9, fontWeight: 700, border: `1px solid ${st.color}35`, background: `${st.color}12`, color: st.color, whiteSpace: "nowrap", flexShrink: 0 }}>{st.label}</span>;
                  })()}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={barTrack}>
                    <div style={{ height: "100%", borderRadius: 4, width: `${pct}%`, background: color, transition: "width 0.6s ease" }} />
                  </div>
                  <span style={{ color: "#334155", fontSize: 10, whiteSpace: "nowrap" }}>
                    {fechados}/{p.tickets.length}
                  </span>
                </div>
                <span style={{ color, fontWeight: 800, fontSize: 13, textAlign: "right" }}>{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Ticket Drawer ── */}
      {drawer && (() => {
        const cfg = drawerCfg[drawer.tipo];
        const tickets = allDrawerTickets.filter(cfg.filter);
        return (
          <TicketDrawer
            open={true}
            onClose={() => setDrawer(null)}
            title={cfg.title}
            subtitle={`${tickets.length} ticket${tickets.length !== 1 ? "s" : ""}`}
            color={cfg.color}
            fetchId={drawer.fetchId}
            fetchTickets={() => Promise.resolve(tickets)}
          />
        );
      })()}

      {/* ── Linha 4: Desenvolvedores ── */}
      {porDev.length > 0 && (
        <div style={{ ...glass, padding: "18px 20px" }}>
          <SectionTitle>Tickets por Desenvolvedor</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginTop: 14 }}>
            {porDev.map(d => (
              <div key={d.nome} style={devCard}>
                <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 700, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {d.nome}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: "#475569", fontSize: 11 }}>{d.fechados} fechados / {d.total} total</span>
                  <span style={{ color: pctColor(d.pct), fontWeight: 800, fontSize: 12 }}>{d.pct}%</span>
                </div>
                <MiniBar pct={d.pct} color={pctColor(d.pct)} />
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

/* ── Sub-components ──────────────────────────────── */
function KpiCard({ label, value, color, extra, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 90, display: "flex", flexDirection: "column", gap: 4,
        padding: "8px 12px", borderRadius: 10,
        background: active ? `${color}14` : "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? `${color}40` : "rgba(255,255,255,0.05)"}`,
        cursor: onClick ? "pointer" : "default",
        transition: "background 0.15s, border-color 0.15s",
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = active ? `${color}20` : "rgba(255,255,255,0.06)"; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.background = active ? `${color}14` : "rgba(255,255,255,0.03)"; }}
    >
      <div style={{ color: active ? color : "#475569", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}>{value}</div>
      {extra}
    </div>
  );
}

function MiniBar({ pct, color }) {
  return (
    <div style={{ height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{ height: "100%", borderRadius: 3, width: `${Math.min(pct, 100)}%`, background: color, transition: "width 0.5s ease" }} />
    </div>
  );
}

function SectionTitle({ children, color = "#60a5fa" }) {
  return (
    <div style={{ color, fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
      {children}
    </div>
  );
}

function CountdownRing({ seconds, total }) {
  const r = 11, circ = 2 * Math.PI * r;
  const filled = circ * (seconds / total);
  const urgent = seconds <= 15;
  const color  = urgent ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none" stroke={color} strokeWidth={2.5}
          strokeDasharray={circ} strokeDashoffset={circ - filled} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }} />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>próx. atualiz.</span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={page}>
      <div style={{ height: 32, width: 280, borderRadius: 10, background: "rgba(255,255,255,0.04)", marginBottom: 20 }} />
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        {[0,1,2,3,4,5].map(i => <div key={i} style={{ flex: 1, height: 80, borderRadius: 14, background: "rgba(255,255,255,0.04)" }} />)}
      </div>
      <div style={{ height: 200, borderRadius: 14, background: "rgba(255,255,255,0.03)" }} />
    </div>
  );
}

/* ── Styles ───────────────────────────────────────── */
const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const page       = { display: "flex", flexDirection: "column", gap: 12 };
const header     = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const headerLeft = { display: "flex", alignItems: "center", gap: 12 };
const h1         = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };
const badge      = { padding: "3px 12px", borderRadius: 20, background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.25)", color: "#60a5fa", fontSize: 12, fontWeight: 700 };
const kpiRow        = { display: "flex", gap: 12, flexWrap: "wrap" };
const kpiGroup      = { ...glass, flex: 1, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8 };
const kpiGroupLabel = { fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2, color: "#334155" };
const kpiGroupCards = { display: "flex", gap: 10 };
const row2       = { display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" };

const projRow = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "5px 8px", borderRadius: 7,
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(255,255,255,0.04)",
};

const faixaBadge = {
  padding: "2px 10px", borderRadius: 20,
  fontSize: 10, fontWeight: 700, border: "1px solid",
  whiteSpace: "nowrap",
};

const faixaDot = { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 };

const barTrack = {
  flex: 1, height: 8, background: "rgba(255,255,255,0.06)",
  borderRadius: 4, overflow: "hidden",
};

const devCard = {
  ...glass,
  padding: "12px 14px",
};

const errorWrap  = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12 };
const btnSec     = { padding: "9px 20px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.08)", background: "transparent", color: "#64748b", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const refreshBtn = { width: 34, height: 34, borderRadius: 8, border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.08)", color: "#60a5fa", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s, border-color 0.15s", flexShrink: 0, lineHeight: 1 };
